import { useEffect, useRef } from "react";

/** Key for history.state — avoids clashing with other apps */
const STYX_NAV = "styxNav" as const;

export type NarrowOpenChat =
  | null
  | { kind: "dm"; peerId: string }
  | { kind: "vault"; vaultId: string; vaultName?: string }
  | {
      kind: "workspace-topics";
      workspaceId: string;
      workspaceName?: string;
    }
  | {
      kind: "workspace-topic";
      workspaceId: string;
      topicId: string;
      workspaceName?: string;
      topicName?: string;
    };

type StyxHistoryState =
  | { [STYX_NAV]: "chat"; peerId: string }
  | { [STYX_NAV]: "vault"; vaultId: string; vaultName?: string }
  | {
      [STYX_NAV]: "workspaceTopics";
      workspaceId: string;
      workspaceName?: string;
    }
  | {
      [STYX_NAV]: "workspaceTopic";
      workspaceId: string;
      topicId: string;
      workspaceName?: string;
      topicName?: string;
    }
  | { [STYX_NAV]: "settings" };

function isStyxDmState(s: unknown): s is { styxNav: "chat"; peerId: string } {
  return (
    typeof s === "object" &&
    s !== null &&
    STYX_NAV in s &&
    (s as { styxNav?: string }).styxNav === "chat" &&
    typeof (s as { peerId?: unknown }).peerId === "string"
  );
}

function isStyxVaultState(s: unknown): s is { styxNav: "vault"; vaultId: string; vaultName?: string } {
  return (
    typeof s === "object" &&
    s !== null &&
    STYX_NAV in s &&
    (s as { styxNav?: string }).styxNav === "vault" &&
    typeof (s as { vaultId?: unknown }).vaultId === "string"
  );
}

function isStyxWorkspaceTopicState(
  s: unknown,
): s is {
  styxNav: "workspaceTopic";
  workspaceId: string;
  topicId: string;
  workspaceName?: string;
  topicName?: string;
} {
  return (
    typeof s === "object" &&
    s !== null &&
    STYX_NAV in s &&
    (s as { styxNav?: string }).styxNav === "workspaceTopic" &&
    typeof (s as { workspaceId?: unknown }).workspaceId === "string" &&
    typeof (s as { topicId?: unknown }).topicId === "string"
  );
}

function isStyxWorkspaceTopicsState(
  s: unknown,
): s is {
  styxNav: "workspaceTopics";
  workspaceId: string;
  workspaceName?: string;
} {
  return (
    typeof s === "object" &&
    s !== null &&
    STYX_NAV in s &&
    (s as { styxNav?: string }).styxNav === "workspaceTopics" &&
    typeof (s as { workspaceId?: unknown }).workspaceId === "string"
  );
}

function isSettingsState(s: unknown): boolean {
  return (
    typeof s === "object" &&
    s !== null &&
    STYX_NAV in s &&
    (s as { styxNav?: string }).styxNav === "settings"
  );
}

function isAnyStyxOpenState(s: unknown): boolean {
  return (
    isStyxDmState(s) || isStyxVaultState(s) || isStyxWorkspaceTopicsState(s) || isStyxWorkspaceTopicState(s)
  );
}

function serializeOpenChat(c: NarrowOpenChat): string | null {
  if (!c) return null;
  if (c.kind === "dm") return `d:${c.peerId}`;
  if (c.kind === "vault") return `v:${c.vaultId}`;
  if (c.kind === "workspace-topics") return `w:${c.workspaceId}`;
  return `t:${c.workspaceId}:${c.topicId}`;
}

function clearStyxHistoryStateIfPresent(): void {
  const st = window.history.state;
  if (isAnyStyxOpenState(st) || isSettingsState(st)) {
    window.history.replaceState(null, "", window.location.href);
  }
}

function historyStateForOpenChat(c: NarrowOpenChat): StyxHistoryState | null {
  if (!c) return null;
  if (c.kind === "dm") {
    return { [STYX_NAV]: "chat", peerId: c.peerId } satisfies StyxHistoryState;
  }
  if (c.kind === "vault") {
    return { [STYX_NAV]: "vault", vaultId: c.vaultId, vaultName: c.vaultName } satisfies StyxHistoryState;
  }
  if (c.kind === "workspace-topics") {
    return {
      [STYX_NAV]: "workspaceTopics",
      workspaceId: c.workspaceId,
      workspaceName: c.workspaceName,
    } satisfies StyxHistoryState;
  }
  return {
    [STYX_NAV]: "workspaceTopic",
    workspaceId: c.workspaceId,
    topicId: c.topicId,
    workspaceName: c.workspaceName,
    topicName: c.topicName,
  } satisfies StyxHistoryState;
}

export type NarrowHistoryHandlers = {
  onListRoot: () => void;
  onChat: (peerId: string) => void;
  onVault: (vaultId: string, labels?: { vaultName?: string }) => void;
  onWorkspaceTopics: (workspaceId: string, labels?: { workspaceName: string }) => void;
  onWorkspaceTopic: (
    workspaceId: string,
    topicId: string,
    labels?: { workspaceName: string; topicName: string },
  ) => void;
  onSettings: () => void;
};

/**
 * On narrow layouts, map "open chat" to a real history entry so the system back gesture /
 * hardware back (Android PWA) returns to the contact list instead of leaving the app.
 */
export function useNarrowChatHistory(
  enabled: boolean,
  narrow: boolean,
  openChat: NarrowOpenChat,
  settingsOpen: boolean,
  historyHandlers: NarrowHistoryHandlers,
) {
  const prevKeyRef = useRef<string | null>(serializeOpenChat(openChat));
  const prevOpenRef = useRef<NarrowOpenChat>(openChat);
  const prevSettingsRef = useRef(settingsOpen);
  const prevNarrowRef = useRef(narrow);
  const syncFromPopRef = useRef(false);
  const historyHandlersRef = useRef(historyHandlers);
  historyHandlersRef.current = historyHandlers;

  const openKey = serializeOpenChat(openChat);

  useEffect(() => {
    if (!enabled) {
      clearStyxHistoryStateIfPresent();
      prevKeyRef.current = openKey;
      prevOpenRef.current = openChat;
      prevSettingsRef.current = settingsOpen;
      prevNarrowRef.current = narrow;
      return;
    }

    if (!narrow) {
      prevKeyRef.current = openKey;
      prevOpenRef.current = openChat;
      prevSettingsRef.current = settingsOpen;
      prevNarrowRef.current = narrow;
      return;
    }

    if (syncFromPopRef.current) {
      syncFromPopRef.current = false;
      prevKeyRef.current = openKey;
      prevOpenRef.current = openChat;
      prevSettingsRef.current = settingsOpen;
      prevNarrowRef.current = narrow;
      return;
    }

    // Resize: wide → narrow with chat or settings already open — add a history layer if missing
    if (narrow && !prevNarrowRef.current) {
      const st = window.history.state;
      if (openChat && !isAnyStyxOpenState(st) && !isSettingsState(st)) {
        const next = historyStateForOpenChat(openChat);
        if (next) {
          window.history.pushState(next, "", window.location.href);
        }
        prevKeyRef.current = openKey;
        prevOpenRef.current = openChat;
        prevSettingsRef.current = settingsOpen;
        prevNarrowRef.current = narrow;
        return;
      }
      if (!openChat && settingsOpen && !isSettingsState(st)) {
        window.history.pushState({ [STYX_NAV]: "settings" } satisfies StyxHistoryState, "", window.location.href);
        prevKeyRef.current = openKey;
        prevOpenRef.current = openChat;
        prevSettingsRef.current = settingsOpen;
        prevNarrowRef.current = narrow;
        return;
      }
    }

    const prevKey = prevKeyRef.current;
    const prevOpen = prevOpenRef.current;
    const prevSettings = prevSettingsRef.current;

    // Open chat from list (or from settings — replace settings layer)
    if (prevKey === null && openKey !== null && openChat) {
      const st = window.history.state;
      const next = historyStateForOpenChat(openChat);
      if (next) {
        if (isSettingsState(st)) {
          window.history.replaceState(next, "", window.location.href);
        } else {
          window.history.pushState(next, "", window.location.href);
        }
      }
    } else if (prevKey !== null && openKey !== null && prevKey !== openKey && openChat) {
      const next = historyStateForOpenChat(openChat);
      if (next) {
        const pushTopicOverTopicList =
          prevOpen?.kind === "workspace-topics" && openChat.kind === "workspace-topic";
        if (pushTopicOverTopicList) {
          window.history.pushState(next, "", window.location.href);
        } else {
          window.history.replaceState(next, "", window.location.href);
        }
      }
    } else if (prevKey !== null && openKey === null) {
      // Session reset / logout while chat was open — drop synthetic entry without navigating away
      if (isAnyStyxOpenState(window.history.state)) {
        window.history.replaceState(null, "", window.location.href);
      }
    }

    // Settings fullscreen on narrow (no chat) — own history entry
    if (!openChat) {
      if (!prevSettings && settingsOpen) {
        window.history.pushState({ [STYX_NAV]: "settings" } satisfies StyxHistoryState, "", window.location.href);
      } else if (prevSettings && !settingsOpen) {
        if (isSettingsState(window.history.state)) {
          window.history.replaceState(null, "", window.location.href);
        }
      }
    }

    prevKeyRef.current = openKey;
    prevOpenRef.current = openChat;
    prevSettingsRef.current = settingsOpen;
    prevNarrowRef.current = narrow;
  }, [enabled, narrow, openKey, openChat, settingsOpen]);

  useEffect(() => {
    if (!enabled || !narrow) return;

    const onPopState = (event: PopStateEvent) => {
      const st = event.state ?? window.history.state;
      const h = historyHandlersRef.current;
      syncFromPopRef.current = true;
      if (isStyxDmState(st)) {
        h.onChat(st.peerId);
        return;
      }
      if (isStyxVaultState(st)) {
        const labels = st.vaultName ? { vaultName: st.vaultName } : undefined;
        h.onVault(st.vaultId, labels);
        return;
      }
      if (isStyxWorkspaceTopicsState(st)) {
        const labels = st.workspaceName ? { workspaceName: st.workspaceName } : undefined;
        h.onWorkspaceTopics(st.workspaceId, labels);
        return;
      }
      if (isStyxWorkspaceTopicState(st)) {
        const labels =
          st.workspaceName && st.topicName
            ? { workspaceName: st.workspaceName, topicName: st.topicName }
            : undefined;
        h.onWorkspaceTopic(st.workspaceId, st.topicId, labels);
        return;
      }
      if (isSettingsState(st)) {
        h.onSettings();
        return;
      }
      h.onListRoot();
    };

    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [enabled, narrow]);
}

export function narrowChatBackAction(narrow: boolean): "history" | "dispatch" {
  if (!narrow) return "dispatch";
  const st = window.history.state;
  if (isAnyStyxOpenState(st) || isSettingsState(st)) return "history";
  return "dispatch";
}
